import React, { useEffect, useState } from 'react';
import { Input, Modal } from 'antd';

import { chartRefusal } from '../../services/charts';

// The server's limits (keepr-api utils/chartUtils.js MAX_NAME, MAX_DESCRIPTION).
export const CHART_NAME_MAX = 100;
export const CHART_DESCRIPTION_MAX = 500;

/** "Sales by stage (copy)", held to the name's limit. */
export function copyName(name) {
	const base = typeof name === 'string' && name.trim() ? name.trim() : 'Chart';
	const suffix = ' (copy)';
	return `${base.slice(0, CHART_NAME_MAX - suffix.length)}${suffix}`;
}

/**
 * The window that names a chart: **Rename chart** (its name and
 * description) or **Duplicate chart** (a personal copy under a new name).
 * The name is required, at most 100 characters, unique among the charts it
 * will sit with; a clash, a full tier or any other refusal is said in the
 * window, where the person is still typing (the server's own sentence).
 *
 *   mode      'rename' | 'duplicate'
 *   chart     the chart named
 *   onSubmit  ({ name, description }) => Promise — rejects with the refusal
 */
export default function NameChartModal({ open, mode = 'rename', chart, onSubmit, onClose }) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState(false);
	const duplicating = mode === 'duplicate';

	useEffect(() => {
		if (!open) return;
		setName(duplicating ? copyName(chart?.name) : (chart?.name || ''));
		setDescription(chart?.description || '');
		setError(null);
		setSaving(false);
	}, [open, duplicating, chart]);

	const submit = async () => {
		const trimmed = name.trim();
		if (!trimmed) {
			setError('Give the chart a name.');
			return;
		}
		setSaving(true);
		try {
			await onSubmit({ name: trimmed, description: description.trim() });
			onClose();
		} catch (e) {
			setError(chartRefusal(e, duplicating ? 'Could not duplicate the chart.' : 'Could not rename the chart.'));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			title={duplicating ? 'Duplicate chart' : 'Rename chart'}
			okText={duplicating ? 'Duplicate' : 'Save'}
			onOk={submit}
			confirmLoading={saving}
			onCancel={onClose}
			destroyOnClose
			className='name-chart-modal'
		>
			{duplicating ? (
				<p className='name-chart-modal__hint'>The copy is yours: only you see it.</p>
			) : null}
			<label className='name-chart-modal__label' htmlFor='chart-name'>Name</label>
			<Input
				id='chart-name'
				autoFocus
				maxLength={CHART_NAME_MAX}
				value={name}
				onChange={(event) => { setName(event.target.value); setError(null); }}
				onPressEnter={submit}
			/>
			<label className='name-chart-modal__label' htmlFor='chart-description'>Description</label>
			<Input.TextArea
				id='chart-description'
				maxLength={CHART_DESCRIPTION_MAX}
				autoSize={{ minRows: 2, maxRows: 5 }}
				value={description}
				placeholder='What this chart answers, in a sentence'
				onChange={(event) => { setDescription(event.target.value); setError(null); }}
			/>
			{error ? <div className='name-chart-modal__error' role='alert'>{error}</div> : null}
		</Modal>
	);
}
